import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Divider,
  Paper,
  Chip,
  TextField,
  Breadcrumbs,
  Link,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  IconButton,
  MobileStepper,
  FormHelperText,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  ShoppingCart as CartIcon,
  LocalShipping as ShippingIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  Calculate as CalculateIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import productService from "../services/productService";
import SwipeableViews from "react-swipeable-views";
import { autoPlay } from "react-swipeable-views-utils";
import AddToCartButton from "../components/common/AddToCartButton";
import AddToCalculatorButton from "../components/calculator/AddToCalculatorButton";
import ProductCalculator from "../components/calculator/ProductCalculator";
import { useCalculator } from "../contexts/CalculatorContext";
import { formatCurrency } from "../utils/formatters";
import { useCart } from "../contexts/CartContext";

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

/**
 * Product Details Page component
 * Displays detailed information about a specific product
 * @returns {JSX.Element} The ProductDetailsPage component
 */
const ProductDetailsPage = () => {
  const { t } = useTranslation("common");
  const { t: tProducts } = useTranslation("products");
  const { id } = useParams();
  const { cart, addToCart, updateCartItem } = useCart();
  const { addToCalculator } = useCalculator();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState(0);

  // Image gallery state
  const [activeStep, setActiveStep] = useState(0);
  const [zoomedImage, setZoomedImage] = useState(null);

  // Variable product state
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [variationAttributes, setVariationAttributes] = useState([]);
  const [availableVariations, setAvailableVariations] = useState([]);
  const [variationError, setVariationError] = useState("");

  useEffect(() => {
    // Fetch product details from API using the extracted id
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        // Pass the 'id' from useParams to the service
        const data = await productService.getProductById(id, true);
        console.log("Fetched product data:", data);
        setProduct(data);

        // Handle variable product initialization
        if (data.productType === "variable" && data.variations) {
          console.log(
            "Initializing variable product with variations:",
            data.variations
          );
          initializeVariableProduct(data);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching product details:", err);
        setError(tProducts("details.unavailable"));
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [id, tProducts]);

  /**
   * Initialize variable product data
   * @param {Object} productData - The product data
   */
  const initializeVariableProduct = (productData) => {
    if (!productData.variations || productData.variations.length === 0) {
      console.warn("No variations found for variable product", productData.id);
      return;
    }

    console.log(`Initializing ${productData.variations.length} variations`);

    // Store available variations, sorted numerically by size
    const sortedVariations = [...productData.variations].sort((a, b) => {
      const extractNum = (v) => {
        let name = v.name || "";
        if (name.startsWith(productData.name)) {
          name = name.substring(productData.name.length).replace(/^[\s-]+/, "");
        }
        const match = name.match(/[\d.]+/);
        return match ? parseFloat(match[0]) : Infinity;
      };
      return extractNum(a) - extractNum(b);
    });
    setAvailableVariations(sortedVariations);

    // Create a single synthetic variation attribute for the dropdown
    const variationOptions = [];

    // Process each variation to get a display name
    productData.variations.forEach((variation) => {
      let displayName = variation.name || "";

      // If the name starts with the product name, remove it for cleaner display
      if (displayName.startsWith(productData.name)) {
        displayName = displayName.substring(productData.name.length).trim();
        // Remove leading dash if present
        if (displayName.startsWith("-")) {
          displayName = displayName.substring(1).trim();
        }
      }

      // If no meaningful name, use a default
      if (!displayName) {
        displayName = `Variation ${variation.id.substring(0, 8)}`;
      }

      variationOptions.push({
        id: variation.id,
        name: displayName,
        variation: variation,
      });
    });

    // Create a single attribute for the variation dropdown
    const singleAttribute = {
      id: "variation-selector",
      name: tProducts("details.variations.title"),
      values: variationOptions,
    };

    console.log("Created variation selector:", singleAttribute);

    // Set the variations attribute
    setVariationAttributes([singleAttribute]);

    // Initialize with no selection
    setSelectedAttributes({ "variation-selector": "" });
  };

  /**
   * Handle variation selection change
   * @param {string} variationId - The selected variation ID
   */
  const handleVariationSelect = (variationId) => {
    if (!variationId) {
      setSelectedVariation(null);
      setSelectedAttributes({ "variation-selector": "" });
      setVariationError("");
      return;
    }

    // Find the selected variation
    const selectedVar = availableVariations.find((v) => v.id === variationId);

    if (selectedVar) {
      setSelectedVariation(selectedVar);
      setSelectedAttributes({ "variation-selector": variationId });

      // Check stock
      if (selectedVar.stock <= 0) {
        setVariationError(tProducts("shared.status.outOfStock"));
      } else {
        setVariationError("");
      }
    } else {
      setSelectedVariation(null);
      setVariationError(
        tProducts("details.variations.selectOption", {
          name: tProducts("details.variations.title"),
        })
      );
    }
  };

  // Handle quantity change
  const handleQuantityChange = (event) => {
    const rawValue = event.target.value;

    if (rawValue === "") {
      setQuantity("");
      return;
    }

    const value = parseInt(rawValue);
    if (Number.isNaN(value) || value < 0) return;

    if (selectedVariation) {
      const cartQuantity = getCartQuantityForVariation(selectedVariation.id);
      const availableStock = selectedVariation.stock - cartQuantity;

      if (value <= availableStock) {
        setQuantity(value);
      } else {
        setQuantity(availableStock);
        toast.warning(
          tProducts("details.stockWarningOnly", {
            stock: availableStock,
            inCart: cartQuantity,
          })
        );
      }
    } else {
      setQuantity(value);
    }
  };

  const handleQuantityBlur = () => {
    if (!quantity || quantity < 1) {
      setQuantity(1);
    }
  };

  // Handle increment quantity
  const handleIncrement = () => {
    if (selectedVariation) {
      const cartQuantity = getCartQuantityForVariation(selectedVariation.id);
      const availableStock = selectedVariation.stock - cartQuantity;

      if (quantity < availableStock) {
        setQuantity(quantity + 1);
      } else {
        toast.warning(
          tProducts("details.stockWarningMax", {
            stock: availableStock,
            inCart: cartQuantity,
          })
        );
      }
    }
  };

  // Handle decrement quantity
  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  // Get cart quantity for a variation
  const getCartQuantityForVariation = (variationId) => {
    if (!cart || !cart.items) return 0;

    // Find items in cart that match this variation
    const matchingItems = cart.items.filter((item) => {
      return item.options?.variationId === variationId;
    });

    // Sum up quantities
    return matchingItems.reduce((total, item) => total + item.quantity, 0);
  };

  // Handle add to cart
  const handleAddToCart = () => {
    // In a real app, this would dispatch an action to add the product to the cart
    console.log(`Added ${quantity} of product ${id} to cart`);
    // Show success message or redirect to cart
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle image navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  // Handle image zoom
  const handleImageZoom = (imageUrl) => {
    setZoomedImage(imageUrl);
  };

  const handleCloseZoom = () => {
    setZoomedImage(null);
  };

  // Get all product images (primary + gallery)
  const getAllProductImages = () => {
    if (!product) return [];

    const images = [];

    // If we have a selected variation with images, use those
    if (
      selectedVariation &&
      selectedVariation.images &&
      selectedVariation.images.length > 0
    ) {
      // Add variation images
      const sortedImages = [...selectedVariation.images].sort(
        (a, b) => a.displayOrder - b.displayOrder
      );

      sortedImages.forEach((img) => {
        images.push({
          url: img.imageUrl,
          isPrimary: img.isPrimary,
          alt: img.alt || selectedVariation.name,
          caption: img.caption,
        });
      });

      // If no primary image in variation, add parent image first
      if (!sortedImages.some((img) => img.isPrimary) && product.imageUrl) {
        images.unshift({
          url: product.imageUrl,
          isPrimary: true,
          alt: product.name,
        });
      }
    } else {
      // Add primary image first
      if (product.imageUrl) {
        images.push({
          url: product.imageUrl,
          isPrimary: true,
          alt: product.name,
        });
      }

      // Add gallery images
      if (product.images && product.images.length > 0) {
        // Sort by display order
        const sortedImages = [...product.images].sort(
          (a, b) => a.displayOrder - b.displayOrder
        );

        // Add non-primary images
        sortedImages.forEach((img) => {
          if (!img.isPrimary) {
            images.push({
              url: img.imageUrl,
              isPrimary: false,
              alt: img.alt || product.name,
              caption: img.caption,
            });
          }
        });
      }
    }

    // If no images, add placeholder
    if (images.length === 0) {
      images.push({
        url: "https://via.placeholder.com/600x400?text=No+Image",
        isPrimary: true,
        alt: t("imageAlt.noImage"),
      });
    }

    return images;
  };

  /**
   * Get effective price based on product type and selected variation
   * @returns {number} The effective price
   */
  const getEffectivePrice = () => {
    if (product?.productType === "variable") {
      if (selectedVariation && selectedVariation.price) {
        return parseFloat(selectedVariation.price);
      }

      // If no variation selected, show price range in UI via getPriceDisplay
      return 0;
    }
    return product?.price > 0 ? parseFloat(product.price) : 0;
  };

  /**
   * Get effective stock based on product type and selected variation
   * @returns {number} The effective stock
   */
  const getEffectiveStock = () => {
    if (product?.productType === "variable") {
      if (selectedVariation && selectedVariation.stock !== undefined) {
        return parseInt(selectedVariation.stock);
      }
      // If no variation selected, stock is 0 until a variation is selected
      return 0;
    }
    return product?.stock || 0;
  };

  /**
   * Get price display for product
   * @returns {string} Formatted price display
   */
  const getPriceDisplay = () => {
    if (product?.productType === "variable") {
      if (selectedVariation) {
        return formatCurrency(parseFloat(selectedVariation.price));
      }

      // If no variation selected, show price range
      if (availableVariations && availableVariations.length > 0) {
        const prices = availableVariations
          .map((v) => parseFloat(v.price))
          .filter((p) => !isNaN(p) && p > 0);

        if (prices.length > 0) {
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);

          if (minPrice === maxPrice) {
            return formatCurrency(minPrice);
          } else {
            return `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`;
          }
        }
      }

      // Fallback if no prices available
      return tProducts("details.priceVariation");
    }

    return formatCurrency(parseFloat(product.price));
  };

  /**
   * Validates if the product can be added to cart
   * @returns {boolean} Whether product can be added to cart
   */
  const canAddToCart = () => {
    if (product?.productType === "variable") {
      return selectedVariation && selectedVariation.stock > 0;
    }
    return product && product.stock > 0;
  };

  /**
   * Handle add to cart for variable products
   */
  const handleAddVariableToCart = () => {
    if (!selectedVariation) {
      setVariationError(
        tProducts("details.variations.selectOption", {
          name: tProducts("details.variations.title"),
        })
      );
      return;
    }

    if (selectedVariation.stock <= 0) {
      setVariationError(tProducts("shared.status.outOfStock"));
      return;
    }

    // Here we would add the selected variation to cart
    console.log(
      `Added ${quantity} of variation ${selectedVariation.id} to cart`
    );
  };

  /**
   * Get combined attributes from parent product and selected variation
   * @returns {Array} Combined attributes list
   */
  const getCombinedAttributes = () => {
    if (!product?.attributes) {
      return [];
    }

    // For simple products, just return all attributes
    if (product.productType !== "variable" || !selectedVariation) {
      return product.attributes;
    }

    // For variable products with selected variation,
    // combine non-variation attributes from parent with variation-specific attributes

    // First, collect all attributes from the parent product
    const resultAttributes = [...product.attributes];

    // Then, add or override with attributes from the selected variation
    if (selectedVariation.attributes) {
      selectedVariation.attributes.forEach((varAttr) => {
        if (varAttr.ProductAttribute?.value) {
          // Check if this attribute already exists in the parent
          const existingIndex = resultAttributes.findIndex(
            (attr) => attr.id === varAttr.id
          );

          if (existingIndex >= 0) {
            // Update the existing attribute with the variation's value
            resultAttributes[existingIndex] = {
              ...resultAttributes[existingIndex],
              ProductAttribute: {
                ...(resultAttributes[existingIndex].ProductAttribute || {}),
                value: varAttr.ProductAttribute.value,
              },
            };
          } else {
            // Add this as a new attribute
            resultAttributes.push(varAttr);
          }
        }
      });
    }

    return resultAttributes;
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="warning">{tProducts("catalog.emptyList")}</Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/products")}
          sx={{ mt: 2 }}
        >
          {tProducts("catalog.title")}
        </Button>
      </Container>
    );
  }

  const allImages = getAllProductImages();
  const maxSteps = allImages.length;
  const isVariableProduct = product.productType === "variable";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="/" underline="hover">
          {t("navigation.home")}
        </Link>
        <Link color="inherit" href="/products" underline="hover">
          {tProducts("catalog.title")}
        </Link>
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Product Images */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2, mb: 2, position: "relative" }}>
            <AutoPlaySwipeableViews
              axis="x"
              index={activeStep}
              onChangeIndex={handleStepChange}
              enableMouseEvents
              autoplay={false}
            >
              {allImages.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    height: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={image.url}
                    alt={
                      image.alt || t("imageAlt.thumbnail", { index: index + 1 })
                    }
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      cursor: "zoom-in",
                    }}
                    onClick={() => handleImageZoom(image.url)}
                  />
                </Box>
              ))}
            </AutoPlaySwipeableViews>
            <IconButton
              sx={{
                position: "absolute",
                top: "50%",
                right: 8,
                transform: "translateY(-50%)",
                zIndex: 1,
                bgcolor: "rgba(255,255,255,0.7)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
              }}
              onClick={handleNext}
              disabled={activeStep === maxSteps - 1}
            >
              <KeyboardArrowRight />
            </IconButton>
            <IconButton
              sx={{
                position: "absolute",
                top: "50%",
                left: 8,
                transform: "translateY(-50%)",
                zIndex: 1,
                bgcolor: "rgba(255,255,255,0.7)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
              }}
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              <KeyboardArrowLeft />
            </IconButton>
            <IconButton
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                bgcolor: "rgba(255,255,255,0.7)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
              }}
              onClick={() => handleImageZoom(allImages[activeStep].url)}
            >
              <ZoomInIcon />
            </IconButton>
          </Paper>

          {/* Thumbnail Navigation */}
          {allImages.length > 1 && (
            <Grid container spacing={1}>
              {allImages.map((image, index) => (
                <Grid item xs={3} sm={2} key={index}>
                  <Paper
                    elevation={activeStep === index ? 3 : 1}
                    sx={{
                      p: 0.5,
                      border: activeStep === index ? "2px solid" : "1px solid",
                      borderColor:
                        activeStep === index ? "primary.main" : "divider",
                    }}
                  >
                    <Box
                      component="img"
                      src={image.url}
                      alt={t("imageAlt.thumbnail", { index: index + 1 })}
                      sx={{
                        width: "100%",
                        height: 60,
                        objectFit: "cover",
                        cursor: "pointer",
                      }}
                      onClick={() => setActiveStep(index)}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Image Caption */}
          {allImages[activeStep]?.caption && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, textAlign: "center" }}
            >
              {allImages[activeStep].caption}
            </Typography>
          )}
        </Grid>

        {/* Product Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" component="h1" gutterBottom>
            {product.name}
          </Typography>

          {isVariableProduct && (
            <Chip
              label={tProducts("shared.productType.variable")}
              color="primary"
              sx={{ mb: 2 }}
            />
          )}

          <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
            {getPriceDisplay()}
          </Typography>

          <Typography variant="body1" sx={{ mb: 3 }}>
            {product.description}
          </Typography>

          {/* Variation Selection for Variable Products */}
          {isVariableProduct && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {tProducts("details.variations.title")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {availableVariations.map((variation) => {
                  // Get display name (strip product name prefix if present)
                  let displayName = variation.name || "";
                  if (displayName.startsWith(product.name)) {
                    displayName = displayName
                      .substring(product.name.length)
                      .trim();
                    if (displayName.startsWith("-")) {
                      displayName = displayName.substring(1).trim();
                    }
                  }
                  if (!displayName) {
                    displayName = `Variation ${variation.id.substring(0, 8)}`;
                  }

                  const isSelected =
                    selectedAttributes["variation-selector"] === variation.id;
                  const isOutOfStock = variation.stock <= 0;

                  const chip = (
                    <Chip
                      key={variation.id}
                      label={displayName}
                      onClick={
                        isOutOfStock
                          ? undefined
                          : () => handleVariationSelect(variation.id)
                      }
                      disabled={isOutOfStock}
                      color={isSelected ? "primary" : "default"}
                      variant={isSelected ? "filled" : "outlined"}
                      sx={{
                        minWidth: 70,
                        fontWeight: isSelected ? 600 : 400,
                        textDecoration: isOutOfStock ? "line-through" : "none",
                        cursor: isOutOfStock ? "not-allowed" : "pointer",
                      }}
                    />
                  );

                  return isOutOfStock ? (
                    <Tooltip
                      key={variation.id}
                      title={tProducts("shared.status.outOfStock")}
                      arrow
                    >
                      <span>{chip}</span>
                    </Tooltip>
                  ) : (
                    chip
                  );
                })}
              </Box>
              {variationError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {variationError}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {tProducts("details.availability")}:
              <Chip
                label={
                  getEffectiveStock() > 0
                    ? tProducts("shared.status.inStock")
                    : tProducts("shared.status.outOfStock")
                }
                color={getEffectiveStock() > 0 ? "success" : "error"}
                size="small"
                sx={{ ml: 1 }}
              />
            </Typography>
            <Typography variant="body2">
              {getEffectiveStock() > 0
                ? tProducts("details.itemsAvailable", {
                    count: getEffectiveStock(),
                  })
                : tProducts("details.unavailable")}
            </Typography>
          </Box>

          {/* Product Specifications */}
          {(() => {
            const specs = [];
            if (product.weight)
              specs.push({
                key: tProducts("admin.modules.details.shipping.weight"),
                value: `${product.weight} ct`,
              });
            if (product.dimensions)
              specs.push({
                key: tProducts("admin.modules.details.shipping.dimensions"),
                value: product.dimensions,
              });
            if (
              product.specifications &&
              Array.isArray(product.specifications)
            ) {
              specs.push(
                ...product.specifications.filter((s) => s.key && s.value)
              );
            }
            // Fallback: legacy ProductAttribute values
            if (specs.length === 0 && product.attributes?.length > 0) {
              getCombinedAttributes()
                .filter((attr) => attr.ProductAttribute?.value)
                .forEach((attr) =>
                  specs.push({
                    key: attr.name,
                    value: attr.ProductAttribute.value,
                  })
                );
            }
            if (product.sku)
              specs.unshift({
                key: tProducts("admin.modules.basicInfo.sku"),
                value: product.sku,
              });
            if (specs.length === 0) return null;
            return (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t("tabs.specifications")}
                </Typography>
                <Grid container spacing={1}>
                  {specs.map((spec, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box sx={{ display: "flex", mb: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", mr: 1 }}
                        >
                          {spec.key}:
                        </Typography>
                        <Typography variant="body2">{spec.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
              </Box>
            );
          })()}

          {/* Add to Cart Section */}
          <Box sx={{ mb: 3 }}>
            {isVariableProduct ? (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Tooltip title={t("common.decreaseQuantity")}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleDecrement}
                        disabled={
                          quantity <= 1 ||
                          !selectedVariation ||
                          selectedVariation.stock <= 0
                        }
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <TextField
                    size="small"
                    value={quantity}
                    onChange={handleQuantityChange}
                    onBlur={handleQuantityBlur}
                    inputProps={{
                      min: 1,
                      max: selectedVariation ? selectedVariation.stock : 0,
                      style: { textAlign: "center" },
                    }}
                    disabled={
                      !selectedVariation || selectedVariation.stock <= 0
                    }
                    sx={{ width: "60px", mx: 1 }}
                  />

                  <Tooltip title={t("common.increaseQuantity")}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleIncrement}
                        disabled={
                          !selectedVariation ||
                          quantity >= selectedVariation.stock ||
                          selectedVariation.stock <= 0
                        }
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Box sx={{ ml: 2, display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CartIcon />}
                      onClick={() => {
                        if (selectedVariation) {
                          // First check if this variation already exists in the cart
                          const existingCartItem = cart?.items?.find(
                            (item) =>
                              item.options?.variationId === selectedVariation.id
                          );

                          if (existingCartItem) {
                            // If item exists in cart, update its quantity instead
                            const newQuantity =
                              existingCartItem.quantity + quantity;
                            // Check if we have enough stock
                            if (newQuantity <= selectedVariation.stock) {
                              console.log("Updating existing cart item:", {
                                itemId: existingCartItem.id,
                                quantity: newQuantity,
                              });
                              // Use the updateCartItem function from CartContext
                              updateCartItem(existingCartItem.id, newQuantity);
                            } else {
                              toast.warning(
                                `Cannot add ${quantity} more. Maximum available stock is ${selectedVariation.stock}`
                              );
                            }
                          } else {
                            // If item doesn't exist, add as new
                            console.log("Adding new item to cart:", {
                              productId: product.id,
                              quantity,
                              options: {
                                variationId: selectedVariation.id,
                                variationName: selectedVariation.name,
                                isVariation: true,
                              },
                            });
                            addToCart(product.id, quantity, {
                              variationId: selectedVariation.id,
                              variationName: selectedVariation.name,
                              isVariation: true,
                            });
                          }
                        }
                      }}
                      disabled={
                        !selectedVariation ||
                        selectedVariation.stock <= 0 ||
                        !quantity ||
                        quantity < 1
                      }
                    >
                      {t("buttons.addToCart")}
                    </Button>

                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<CalculateIcon />}
                      onClick={() => {
                        if (selectedVariation) {
                          addToCalculator(product, selectedVariation, quantity);
                        }
                      }}
                      disabled={
                        !selectedVariation ||
                        selectedVariation.stock <= 0 ||
                        !quantity ||
                        quantity < 1
                      }
                    >
                      {t("buttons.addToCalculator")}
                    </Button>
                  </Box>
                </Box>

                {variationError && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {variationError}
                  </Typography>
                )}
                {!selectedVariation && !variationError && (
                  <Typography variant="body2" color="text.secondary">
                    {tProducts("details.selectVariationPrompt")}
                  </Typography>
                )}
              </Box>
            ) : (
              <AddToCartButton
                productId={product.id}
                stock={product.stock}
                minOrderQuantity={1}
                fullWidth={false}
                showQuantity={true}
                buttonText={t("buttons.addToCart")}
              />
            )}
          </Box>

          {/* Additional Info */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ShippingIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {t("info.freeShipping", { amount: formatCurrency(1000) })}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <SecurityIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {t("info.securePayment")}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Product Calculator - Only shown for variable products (full width) */}
      {isVariableProduct && (
        <Box sx={{ mb: 4, mt: 4 }}>
          <ProductCalculator />
        </Box>
      )}

      {/* Product Tabs */}
      <Box sx={{ mt: 6, mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t("tabs.description")} />
          <Tab label={t("tabs.specifications")} />
        </Tabs>

        <Box sx={{ p: 3, border: 1, borderColor: "divider", borderTop: 0 }}>
          {activeTab === 0 && (
            <Typography variant="body1">{product.description}</Typography>
          )}

          {activeTab === 1 && (
            <Box>
              {(() => {
                const specs = [];
                if (product.weight)
                  specs.push({
                    key: tProducts(
                      "admin.modules.details.shipping.weight"
                    ),
                    value: `${product.weight} ct`,
                  });
                if (product.dimensions)
                  specs.push({
                    key: tProducts(
                      "admin.modules.details.shipping.dimensions"
                    ),
                    value: product.dimensions,
                  });
                if (
                  product.specifications &&
                  Array.isArray(product.specifications)
                ) {
                  specs.push(
                    ...product.specifications.filter(
                      (s) => s.key && s.value
                    )
                  );
                }
                // Fallback: legacy ProductAttribute values
                if (
                  specs.length === 0 &&
                  product.attributes?.length > 0
                ) {
                  getCombinedAttributes()
                    .filter((attr) => attr.ProductAttribute?.value)
                    .forEach((attr) =>
                      specs.push({
                        key: attr.name,
                        value: attr.ProductAttribute.value,
                      })
                    );
                }
                if (product.sku)
                  specs.unshift({
                    key: tProducts("admin.modules.basicInfo.sku"),
                    value: product.sku,
                  });
                if (specs.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      {tProducts("details.noSpecifications")}
                    </Typography>
                  );
                }
                return specs.map((spec, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      py: 1,
                      borderBottom:
                        index < specs.length - 1 ? 1 : 0,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ width: "40%", fontWeight: "bold" }}
                    >
                      {spec.key}
                    </Typography>
                    <Typography variant="body2" sx={{ width: "60%" }}>
                      {spec.value}
                    </Typography>
                  </Box>
                ));
              })()}
            </Box>
          )}
        </Box>
      </Box>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
          onClick={handleCloseZoom}
        >
          <Box
            component="img"
            src={zoomedImage}
            alt={t("imageAlt.zoomedProduct")}
            sx={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
            }}
          />
          <IconButton
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
            }}
            onClick={handleCloseZoom}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </Container>
  );
};

export default ProductDetailsPage;
